import apiClient from './client';
import type {
  User,
  ChangePasswordDto,
  BindEmailDto,
  BindPhoneDto,
  EmailLoginDto,
  EmailRegisterDto,
  PhoneLoginDto,
  PasswordLoginDto,
  PhoneRegisterDto,
  RegisterDto,
  ResetPasswordEmailDto,
  ResetPasswordDto,
  SendBindEmailCodeDto,
  SendBindPhoneCodeDto,
  SendEmailCodeDto,
  SendRegisterEmailCodeDto,
  SendRegisterPhoneCodeDto,
  SendResetPasswordEmailDto,
  SendResetPasswordSmsDto,
  SmsCaptchaChallengeDto,
  SendSmsCodeDto,
  SendSmsCodeResponse,
  SetPasswordDto,
  SmsCaptchaChallengeResponse,
  SmsCaptchaVerifyDto,
  SmsCaptchaVerifyResponse,
  AuthResponse,
  BackendAuthResponse,
  BackendUser,
  RegisterContactCheckDto,
  RegisterContactCheckResponse,
  UsernameCheckResponse,
  UpdateUserDto,
  UpdateUsernameDto,
  UploadAvatarDto,
} from '@/types/user';
import { attachmentsApi } from './attachments';

const mapBackendUserToUser = (backendUser: BackendUser): User => {
  return {
    userId: backendUser.userId,
    username: backendUser.username,
    phone: backendUser.phone,
    countryCode: backendUser.countryCode,
    phoneVerified: backendUser.phoneVerified,
    email: backendUser.email || undefined,
    emailVerified: backendUser.emailVerified,
    accountIdentifier:
      backendUser.accountIdentifier ||
      backendUser.phone ||
      backendUser.email ||
      backendUser.username,
    usernameChanged: backendUser.usernameChanged,
    nickname: backendUser.nickname || undefined,
    avatarUrl: backendUser.avatarUrl || undefined,
    aiCreditsBalance: backendUser.aiCreditsBalance,
    createdAt: backendUser.createdAt,
    ownedTeamCount: backendUser.ownedTeamCount ?? undefined,
    maxOwnedTeams: backendUser.maxOwnedTeams ?? undefined,
    certStatus: backendUser.certStatus ?? undefined,
    hasPassword: backendUser.hasPassword ?? undefined,
    passwordUpdatedAt: backendUser.passwordUpdatedAt ?? null,
    shortcuts: backendUser.shortcuts,
  };
};

export const authApi = {
  /**
   * 创建短信图形验证码 challenge
   */
  createSmsCaptchaChallenge: async (
    data: SmsCaptchaChallengeDto
  ): Promise<SmsCaptchaChallengeResponse> => {
    return apiClient.post('/auth/captcha/challenge', {
      phone: data.phone,
      countryCode: data.countryCode,
      purpose: data.purpose,
    });
  },

  /**
   * 校验短信图形验证码并换取发送凭证
   */
  verifySmsCaptchaChallenge: async (
    data: SmsCaptchaVerifyDto
  ): Promise<SmsCaptchaVerifyResponse> => {
    return apiClient.post('/auth/captcha/verify', {
      captchaId: data.captchaId,
      dragX: data.dragX,
      dragY: data.dragY,
      durationMs: data.durationMs,
    });
  },

  /**
   * 发送短信验证码
   */
  sendSmsCode: async (data: SendSmsCodeDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/auth/sms/send', {
      phone: data.phone,
      countryCode: data.countryCode,
      purpose: data.purpose,
      smsProofId: data.smsProofId,
    });
  },

  /**
   * 发送手机号注册验证码（泛化响应，不暴露手机号是否已注册）
   */
  sendRegisterPhoneCode: async (data: SendRegisterPhoneCodeDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/auth/register/phone/send-sms', {
      phone: data.phone,
      countryCode: data.countryCode,
      smsProofId: data.smsProofId,
    });
  },

  /**
   * 手机号登录
   */
  loginWithPhone: async (data: PhoneLoginDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>('/auth/login/phone', {
      phone: data.phone,
      countryCode: data.countryCode,
      code: data.code,
      inviteCode: data.inviteCode,
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 用户名密码登录
   */
  loginWithPassword: async (data: PasswordLoginDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>('/auth/login/password', {
      username: data.username,
      password: data.password,
      countryCode: data.countryCode,
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 邮箱密码登录
   */
  loginWithEmail: async (data: EmailLoginDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>('/auth/login/email', {
      email: data.email,
      password: data.password,
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 账号密码注册
   */
  register: async (data: RegisterDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>('/auth/register', {
      phone: data.phone,
      countryCode: data.countryCode,
      code: data.code,
      username: data.username,
      password: data.password,
      inviteCode: data.inviteCode,
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 手机号注册
   */
  registerWithPhone: async (data: PhoneRegisterDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>('/auth/register/phone', {
      phone: data.phone,
      countryCode: data.countryCode,
      code: data.code,
      inviteCode: data.inviteCode,
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 邮箱注册
   */
  registerWithEmail: async (data: EmailRegisterDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>('/auth/register/email', {
      email: data.email,
      code: data.code,
      password: data.password,
      inviteCode: data.inviteCode,
    });

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 发送邮箱验证码
   */
  sendEmailCode: async (data: SendEmailCodeDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/auth/email/send', {
      email: data.email,
      purpose: data.purpose,
      verificationProofId: data.verificationProofId,
    });
  },

  /**
   * 发送邮箱注册验证码（泛化响应，不暴露邮箱是否已注册）
   */
  sendRegisterEmailCode: async (data: SendRegisterEmailCodeDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/auth/register/email/send', {
      email: data.email,
    });
  },

  /**
   * 检查用户名是否可用于注册
   */
  checkUsername: async (username: string): Promise<UsernameCheckResponse> => {
    return apiClient.get('/auth/username/check', {
      params: { username },
    });
  },

  /**
   * 检查手机号/邮箱是否可用于注册
   */
  checkRegisterContact: async (
    data: RegisterContactCheckDto
  ): Promise<RegisterContactCheckResponse> => {
    return apiClient.get('/auth/register/contact/check', {
      params: {
        mode: data.mode,
        phone: data.phone,
        countryCode: data.countryCode,
        email: data.email,
      },
    });
  },

  /**
   * 设置初始密码
   */
  setPassword: async (data: SetPasswordDto): Promise<User> => {
    const response = await apiClient.post<unknown, BackendUser>('/auth/password/set', {
      password: data.password,
    });
    return mapBackendUserToUser(response);
  },

  /**
   * 修改密码
   */
  changePassword: async (data: ChangePasswordDto): Promise<User> => {
    const response = await apiClient.put<unknown, BackendUser>('/auth/password', {
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
    });
    return mapBackendUserToUser(response);
  },

  /**
   * 发送找回密码短信
   */
  sendResetPasswordSms: async (data: SendResetPasswordSmsDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/auth/password/reset/send-sms', {
      phone: data.phone,
      countryCode: data.countryCode,
      smsProofId: data.smsProofId,
    });
  },

  /**
   * 发送找回密码邮箱验证码
   */
  sendResetPasswordEmail: async (data: SendResetPasswordEmailDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/auth/password/reset/send-email', {
      email: data.email,
    });
  },

  /**
   * 找回密码并自动登录
   */
  resetPassword: async (data: ResetPasswordDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>(
      '/auth/password/reset/verify',
      {
        phone: data.phone,
        countryCode: data.countryCode,
        code: data.code,
        newPassword: data.newPassword,
      }
    );

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 邮箱找回密码并自动登录
   */
  resetPasswordWithEmail: async (data: ResetPasswordEmailDto): Promise<AuthResponse> => {
    const response = await apiClient.post<unknown, BackendAuthResponse>(
      '/auth/password/reset/verify-email',
      {
        email: data.email,
        code: data.code,
        newPassword: data.newPassword,
      }
    );

    return {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType,
      user: mapBackendUserToUser(response.user),
    };
  },

  /**
   * 退出登录
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  /**
   * 获取当前用户信息
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<unknown, BackendUser>('/users/me');
    return mapBackendUserToUser(response);
  },

  /**
   * 更新用户信息
   */
  updateProfile: async (data: UpdateUserDto): Promise<User> => {
    const response = await apiClient.put<unknown, BackendUser>('/users/me', data);
    return mapBackendUserToUser(response);
  },

  /**
   * 修改用户名
   */
  updateUsername: async (data: UpdateUsernameDto): Promise<User> => {
    const response = await apiClient.put<unknown, BackendUser>('/users/me/username', data);
    return mapBackendUserToUser(response);
  },

  /**
   * 上传头像
   */
  uploadAvatar: async ({ file, userId }: UploadAvatarDto): Promise<string> => {
    const response = await attachmentsApi.upload({
      file,
      category: 'image',
      relatedId: userId,
      relatedType: 'user',
      attachmentPurpose: 'avatar',
    });

    return response.fullUrl || response.fileUrl || '';
  },

  /**
   * 绑定邮箱
   */
  bindEmail: async (data: BindEmailDto): Promise<User> => {
    const response = await apiClient.post<unknown, BackendUser>('/users/me/bind/email', data);
    return mapBackendUserToUser(response);
  },

  /**
   * 发送绑定邮箱验证码（泛化响应，不暴露邮箱是否已占用）
   */
  sendBindEmailCode: async (data: SendBindEmailCodeDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/users/me/bind/email/send', {
      email: data.email,
    });
  },

  /**
   * 绑定手机号
   */
  bindPhone: async (data: BindPhoneDto): Promise<User> => {
    const response = await apiClient.post<unknown, BackendUser>('/users/me/bind/phone', data);
    return mapBackendUserToUser(response);
  },

  /**
   * 发送绑定手机号验证码（泛化响应，不暴露手机号是否已占用）
   */
  sendBindPhoneCode: async (data: SendBindPhoneCodeDto): Promise<SendSmsCodeResponse> => {
    return apiClient.post('/users/me/bind/phone/send-sms', {
      phone: data.phone,
      countryCode: data.countryCode,
      smsProofId: data.smsProofId,
    });
  },
};
